import { useParams, useLocation } from '@fs/zion-router'
import Cache from '@fs/zion-cache'
import { mutate } from 'swr'
import type { Person, PersonId, PID } from '@fs/zion-tree-types'
import { useEffect, useRef } from 'react'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { useFocusPID } from '../providers/FocusPIDContext'
import { useShowTempleContext } from '../providers/ShowTempleContext'
import type { SwrResponse } from './useSwrRequest'
import { useSwrRequest } from './useSwrRequest'
import { mutateChangeLogSummary } from './useChangeLogSummary'
import { readPerson } from './emulators/personBE'

type CardType = 'card' | 'summary'

// We only need to set this once per page load, but we can't use a hook and provider to do it safely.
let isMember = false

const personsCache = new Cache({ storeName: 'persons_r9', encrypted: process.env.NODE_ENV === 'production' })
const swrKey = (pid: string, treeId: string, includeTemple: boolean, cardType: CardType): string => {
  const includeTempleQuery = includeTemple ? 'includeTempleRollupStatus=true' : undefined
  const queries = [includeTempleQuery, `enforceTemplePolicyEx=true`, `treeId=${treeId}`].filter(Boolean).join('&')
  return `/service/tree/tree-data/v8/person/${pid}/${cardType}?${queries}`
}

const timeoutTimesMs = [500, 1000, 3000, 5000, 8000]

export default function useFocusPerson(): SwrResponse<Person & { id: PID }> & {
  notFound: boolean
  focusPerson?: Person & { id: PID } // TODO now that we do summary if on RH page, type can also be SummaryPerson!!! Maybe we want to use a new hook, useFocusSummaryPerson for SummaryPerson.
} {
  const retryCount = useRef(0)
  const { pid } = useParams()
  const { currentTreeId } = useGroupsContext()
  const alreadyLoadedFocusPerson = useRef(false)
  const location = useLocation()
  const isOnRHPage = location.pathname.includes('/research-help/')
  if (!isOnRHPage) alreadyLoadedFocusPerson.current = true
  const cardType = isOnRHPage && !alreadyLoadedFocusPerson.current ? 'summary' : 'card'

  // When useFocusPerson is used inside of a MemoryRouter, the 'pid' param likely doesn't exist.
  // The FocusPIDContext provides it at a higher level that is still accessible inside of the MemoryRouter.
  const focusPID = useFocusPID()
  const [showTemple, isTempleLoading] = useShowTempleContext()

  if (!isMember && showTemple && cardType !== 'summary') isMember = true

  const response = useSwrRequest<Person & { id: PID }>(
    isTempleLoading === 'loading'
      ? null
      : swrKey(pid || focusPID, currentTreeId || 'PRIVATE', isMember, cardType ?? 'card'),
    personsCache,
    {
      dedupingInterval: 10000, // Dedupe fetching the person by 10 seconds
      focusThrottleInterval: 60 * 1000 * 2, // Only refetch the person when the tab gets refocused if more than 2 minutes has passed since last refetch
      emulator: readPerson,
    }
  )

  const notFound =
    !!response.error?.response?.status && response.error.response.status >= 400 && response.error.response.status < 500

  // Refetch until templeRollupStatus is no longer CALCULATING or retried 5 times.
  useEffect(() => {
    let timeoutId: NodeJS.Timeout
    if (retryCount.current < 5 && response.data?.templeRollupStatus === 'CALCULATING') {
      const timeoutMs = timeoutTimesMs[retryCount.current]
      timeoutId = setTimeout(() => {
        retryCount.current = retryCount.current++
        mutate(swrKey(pid, currentTreeId || 'PRIVATE', isMember, cardType ?? 'card'))
      }, timeoutMs)
    } else if (
      retryCount.current > 0 &&
      response.data?.templeRollupStatus &&
      response.data.templeRollupStatus !== 'CALCULATING'
    ) {
      retryCount.current = 0
    }
    return () => {
      timeoutId && clearTimeout(timeoutId)
    }
  }, [pid, cardType, response.data?.templeRollupStatus, currentTreeId])

  return {
    ...response,
    notFound,
    focusPerson: response.data,
    isLoading: isTempleLoading === 'loading' || response.isLoading,
  }
}

export function mutateFocusPerson(pid: PersonId, treeId: string): void {
  // This is only used outside of the RH page so the card type should always be card.
  mutate(swrKey(pid, treeId, isMember, 'card'))
  mutateChangeLogSummary(pid, treeId)
}
